import { REGROUPEMENTS, type GroupeDAnalyse, type Regroupement } from '@nextilt/commun';
import type { BulleEnrichie, Feuille } from '../partage/api/types';
import { moisEnLettres } from '../partage/datesEnMots';
import {
  GROUPE_SANS_CONTACT,
  GROUPE_SANS_DATE,
  GROUPE_SANS_LIEU,
  MOTS_DES_ETATS_D_ANALYSE,
} from '../partage/vocabulaire';
import { bullesConnues } from './contenuDesMarges';

/*
 * **Ce que l'écran garde de l'Analyse** (chantier analyse-au-serveur, modification 02) : le
 * serveur compte (`server/src/domain/carte/analyse.ts`, le panneau et chaque bulle dans
 * `Feuille.analyse`, les groupes par `GET /api/feuille/:id/groupes`) ; l'écran fait tourner G, et
 * dit les noms des groupes. Sans rendu, testé dans `tests/client/projets/groupesALEcran.test.ts`.
 */

export type { Regroupement } from '@nextilt/commun';

/** G : le regroupement qui suit ; après le mois, `null`, la carte d'avant. */
export function regroupementSuivant(avant: Regroupement | null): Regroupement | null {
  if (avant === null) return REGROUPEMENTS[0] ?? null;
  return REGROUPEMENTS[REGROUPEMENTS.indexOf(avant) + 1] ?? null;
}

const NOM_DU_GROUPE_SANS: Record<Exclude<Regroupement, 'etat'>, string> = {
  contact: GROUPE_SANS_CONTACT,
  lieu: GROUPE_SANS_LIEU,
  mois: GROUPE_SANS_DATE,
};

/**
 * Le nom d'un groupe à l'écran : celui que le serveur rend quand c'est une donnée (le contact, le
 * lieu tel qu'écrit) ; sinon le mot de l'état, le mois en lettres, ou celui du groupe « sans »
 * (Personne, Nulle part, Sans date), dont la clé est vide.
 */
export function nomDuGroupe(groupe: Pick<GroupeDAnalyse, 'cle' | 'nom'>, par: Regroupement): string {
  if (groupe.nom !== null) return groupe.nom;
  if (par === 'etat') return MOTS_DES_ETATS_D_ANALYSE[groupe.cle as keyof typeof MOTS_DES_ETATS_D_ANALYSE] ?? groupe.cle;
  if (groupe.cle === '') return NOM_DU_GROUPE_SANS[par];
  return par === 'mois' ? moisEnLettres(groupe.cle) : groupe.cle;
}

/** Un groupe tel que l'écran le montre : son nom dit, ses bulles retrouvées dans la feuille. */
export interface GroupeALEcran extends Omit<GroupeDAnalyse, 'nom' | 'bulles'> {
  nom: string;
  bulles: BulleEnrichie[];
}

/** Les groupes du serveur, nommés, leurs bulles lues parmi celles que la feuille connaît (dans leur ordre). */
export function groupesALEcran(
  groupes: readonly GroupeDAnalyse[],
  par: Regroupement,
  feuille: Pick<Feuille, 'enfants' | 'carte'>
): GroupeALEcran[] {
  const parId = new Map(bullesConnues(feuille).map((b) => [b.id, b]));
  return groupes.map((g) => ({
    ...g,
    nom: nomDuGroupe(g, par),
    bulles: g.bulles.flatMap((id) => parId.get(id) ?? []),
  }));
}
